import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

interface EntryScoreData {
  distribution: {
    total: number;
    min: number;
    p50: number;
    p70: number;
    p85: number;
    p90: number;
    max: number;
    avg: number;
  };
  histogram: Array<{ range: string; count: number }>;
  confidenceBreakdown: Record<string, number>;
  regimeBreakdown: Record<string, number>;
  suggestedThresholds: {
    conservative: number;
    balanced: number;
    aggressive: number;
  };
  metadata: {
    lookbackDays: number;
    requestedLimit: number;
    totalRecordsFound: number;
    uniqueSignals: number;
    analyzedSignals: number;
    dateRange: {
      from: string;
      to: string;
    };
  };
}

interface PolicyThresholds {
  risk_on: {
    buy_min: number;
    low: number;
    med: number;
    med_high: number;
    high: number;
    extreme: number;
  };
  neutral: {
    buy_min: number;
    low: number;
    med: number;
    med_high: number;
    high: number;
    extreme: number;
  };
  risk_off: {
    buy_min: number;
    low: number;
    med: number;
    med_high: number;
    high: number;
    extreme: number;
  };
}

interface PolicyData {
  entry_model?: {
    thresholds?: PolicyThresholds;
    confidence_levels?: Record<string, { min_score: string; position_pct: number }>;
  };
}

interface SectorDistribution {
  sector: string;
  count: number;
  percentage: number;
  avgScore: number;
}

const EntryScoreDistribution: React.FC = () => {
  const [data, setData] = useState<EntryScoreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  
  // Policy state
  const [policyThresholds, setPolicyThresholds] = useState<PolicyThresholds | null>(null);
  const [policyConfidenceLevels, setPolicyConfidenceLevels] = useState<Record<string, { min_score: string; position_pct: number }> | null>(null);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [policyLastFetched, setPolicyLastFetched] = useState<Date | null>(null);
  
  // Sector distribution state
  const [sectorDistribution, setSectorDistribution] = useState<SectorDistribution[]>([]);
  const [sectorLoading, setSectorLoading] = useState(true);
  const [sectorError, setSectorError] = useState<string | null>(null);
  const [totalStocksInUniverse, setTotalStocksInUniverse] = useState<number>(0);
  
  // Input state (what user is typing)
  const [daysInput, setDaysInput] = useState<string>('7');
  const [limitInput, setLimitInput] = useState<string>('200');
  
  // Applied state (what was actually fetched)
  const [appliedDays, setAppliedDays] = useState<number>(7);
  const [appliedLimit, setAppliedLimit] = useState<number>(200);
  
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch sector distribution from Trading Universe
  const fetchSectorDistribution = async () => {
    try {
      setSectorLoading(true);
      setSectorError(null);
      
      const response = await api.get('/selectors/trading-universe');
      
      if (response.data && response.data.success && response.data.data?.universe) {
        const stocks = response.data.data.universe;
        setTotalStocksInUniverse(stocks.length);
        
        // Calculate sector distribution
        const sectorCounts: Record<string, { count: number; totalScore: number }> = {};
        
        stocks.forEach((stock: any) => {
          const sector = stock.sector || 'Unknown';
          if (!sectorCounts[sector]) {
            sectorCounts[sector] = { count: 0, totalScore: 0 };
          }
          sectorCounts[sector].count++;
          sectorCounts[sector].totalScore += stock.compositeScore || 0;
        });
        
        // Convert to array and calculate percentages
        const distribution: SectorDistribution[] = Object.entries(sectorCounts)
          .map(([sector, data]) => ({
            sector,
            count: data.count,
            percentage: stocks.length > 0 ? (data.count / stocks.length) * 100 : 0,
            avgScore: data.count > 0 ? data.totalScore / data.count : 0
          }))
          .sort((a, b) => b.count - a.count);
        
        setSectorDistribution(distribution);
      } else {
        setSectorError('No universe data available');
        setSectorDistribution([]);
        setTotalStocksInUniverse(0);
      }
    } catch (err: any) {
      console.error('Sector distribution fetch error:', err);
      setSectorError(err?.message || 'Failed to load sector distribution');
      setSectorDistribution([]);
      setTotalStocksInUniverse(0);
    } finally {
      setSectorLoading(false);
    }
  };

  // Fetch policy thresholds
  const fetchPolicy = async () => {
    try {
      setPolicyLoading(true);
      setPolicyError(null);
      
      const response = await api.get('/admin/policy');
      
      // Debug logging to help diagnose issues
      console.log('[EntryScoreDistribution] Policy API response:', {
        success: response.data?.success,
        hasData: !!response.data?.data,
        hasEntryModel: !!response.data?.data?.entry_model,
        hasThresholds: !!response.data?.data?.entry_model?.thresholds,
        policyVersion: response.data?.data?.version,
        thresholdKeys: response.data?.data?.entry_model?.thresholds 
          ? Object.keys(response.data.data.entry_model.thresholds)
          : [],
        neutralThresholdKeys: response.data?.data?.entry_model?.thresholds?.neutral 
          ? Object.keys(response.data.data.entry_model.thresholds.neutral)
          : []
      });
      
      if (response.data && response.data.success && response.data.data) {
        const policyData: PolicyData = response.data.data;
        if (policyData.entry_model?.thresholds) {
          // Validate that all required threshold keys exist
          const requiredKeys = ['buy_min', 'low', 'med', 'med_high', 'high', 'extreme'];
          const regimes = ['risk_on', 'neutral', 'risk_off'] as const;
          
          for (const regime of regimes) {
            const thresholds = policyData.entry_model.thresholds[regime];
            if (thresholds) {
              const missingKeys = requiredKeys.filter(key => !(key in thresholds));
              if (missingKeys.length > 0) {
                console.warn(`[EntryScoreDistribution] Missing threshold keys in ${regime}:`, missingKeys);
              }
            } else {
              console.warn(`[EntryScoreDistribution] Missing regime in thresholds:`, regime);
            }
          }
          
          console.log('[EntryScoreDistribution] Setting policy thresholds:', policyData.entry_model.thresholds);
          setPolicyThresholds(policyData.entry_model.thresholds);
        } else {
          console.warn('[EntryScoreDistribution] No thresholds in entry_model:', policyData.entry_model);
          setPolicyError('Policy thresholds not found in response');
        }
        if (policyData.entry_model?.confidence_levels) {
          setPolicyConfidenceLevels(policyData.entry_model.confidence_levels);
        }
        setPolicyLastFetched(new Date());
      } else {
        console.error('[EntryScoreDistribution] Invalid policy response:', response.data);
        setPolicyError('Failed to load policy thresholds');
      }
    } catch (err: any) {
      console.error('[EntryScoreDistribution] Policy fetch error:', err);
      setPolicyError(err?.message || 'Failed to load policy');
    } finally {
      setPolicyLoading(false);
    }
  };

  const fetchData = async (days: number, limit: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/admin/entry-score-distribution', {
        params: { days, limit }
      });
      
      if (response.data && response.data.success) {
        if (response.data.data && response.data.data.distribution) {
          setData(response.data.data);
          setAppliedDays(days);
          setAppliedLimit(limit);
        } else if (response.data.data && response.data.data.message) {
          setData(null);
          setAppliedDays(days);
          setAppliedLimit(limit);
        } else {
          setError('Invalid response format from server');
        }
      } else {
        setError(response.data?.error?.message || response.data?.error || 'Failed to load entry score distribution');
      }
      setLastFetched(new Date());
    } catch (err: any) {
      console.error('Entry score distribution fetch error:', err);
      const errorMessage = err?.response?.data?.error?.message 
        || err?.response?.data?.error 
        || err?.message 
        || 'Failed to load entry score distribution';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData(7, 200);
    fetchPolicy();
    fetchSectorDistribution();
  }, []);

  const handleDaysChange = (value: string) => {
    setDaysInput(value);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      const days = parseInt(value, 10);
      if (days && days >= 1 && days <= 90) {
        fetchData(days, parseInt(limitInput, 10) || 200);
      }
    }, 500);
  };

  const handleLimitChange = (value: string) => {
    setLimitInput(value);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      const limit = parseInt(value, 10);
      if (limit && limit >= 10 && limit <= 2000) {
        fetchData(parseInt(daysInput, 10) || 7, limit);
      }
    }, 500);
  };

  const handleRefresh = () => {
    const days = parseInt(daysInput, 10) || 7;
    const limit = parseInt(limitInput, 10) || 200;
    fetchData(days, limit);
    fetchPolicy(); // Also refresh policy
    fetchSectorDistribution(); // Also refresh sector distribution
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Helper to determine if a score passes a threshold
  const _getPassStatus = (score: number, threshold: number): { passes: boolean; margin: number } => {
    const passes = score >= threshold;
    const margin = score - threshold;
    return { passes, margin };
  };
  void _getPassStatus; // Reserved for future use

  // Get the dominant regime from data
  const getDominantRegime = (): 'risk_on' | 'neutral' | 'risk_off' => {
    if (!data?.regimeBreakdown) return 'neutral';
    const entries = Object.entries(data.regimeBreakdown);
    if (entries.length === 0) return 'neutral';
    const sorted = entries.sort(([, a], [, b]) => (b as number) - (a as number));
    if (!sorted[0]) return 'neutral';
    const dominant = sorted[0][0].toLowerCase().replace(' ', '_');
    if (dominant === 'risk_on' || dominant === 'risk on') return 'risk_on';
    if (dominant === 'risk_off' || dominant === 'risk off') return 'risk_off';
    return 'neutral';
  };

  // Calculate what percentage of signals pass each threshold
  const _calculatePassRates = (thresholds: PolicyThresholds[keyof PolicyThresholds], maxScore: number) => {
    if (!data) return null;
    
    const levels = ['buy_min', 'low', 'med', 'med_high', 'high', 'extreme'] as const;
    return levels.map(level => {
      const threshold = thresholds[level];
      const passes = maxScore >= threshold;
      return {
        level,
        threshold,
        passes,
        margin: maxScore - threshold
      };
    });
  };
  void _calculatePassRates; // Reserved for future use

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="bg-neutral-900 rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white mb-2">Entry Score Distribution</h2>
            <p className="text-sm text-neutral-400">
              Analysis of entry scores from recent trading signals to help calibrate policy thresholds
            </p>
            {lastFetched && (
              <p className="text-xs text-neutral-500 mt-2">
                Last updated: {lastFetched.toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })} at {lastFetched.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  timeZoneName: 'short'
                })}
                {appliedDays && appliedLimit && (
                  <span className="ml-2">
                    (showing {appliedDays} days, limit {appliedLimit})
                  </span>
                )}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-neutral-400 mb-1">Lookback (days)</label>
              <input
                type="number"
                value={daysInput}
                onChange={(e) => handleDaysChange(e.target.value)}
                min={1}
                max={90}
                placeholder="7"
                className="bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-white text-sm w-20 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-neutral-400 mb-1">Limit</label>
              <input
                type="number"
                value={limitInput}
                onChange={(e) => handleLimitChange(e.target.value)}
                min={10}
                max={2000}
                placeholder="200"
                className="bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-white text-sm w-24 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-4 p-3 bg-neutral-800/50 rounded-lg border border-neutral-700">
          <p className="text-xs text-neutral-400">
            <strong className="text-neutral-300">Lookback:</strong> Number of days to analyze (1-90). 
            <strong className="text-neutral-300 ml-3">Limit:</strong> Maximum number of signals to include (10-2000).
            <span className="ml-3 text-neutral-500">Changes apply automatically after 0.5s.</span>
          </p>
        </div>
      </div>

      {/* Current Policy Thresholds - Always show if available */}
      {!policyLoading && policyThresholds && (
        <div className="bg-neutral-900 rounded-lg p-6 border border-amber-500/30">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                Current Policy Thresholds
              </h3>
              <p className="text-xs text-neutral-400 mt-1">
                From <code className="bg-neutral-800 px-1.5 py-0.5 rounded">policy.json</code> entry_model.thresholds
                {policyLastFetched && (
                  <span className="ml-2 text-neutral-500">
                    (loaded {policyLastFetched.toLocaleTimeString()})
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={fetchPolicy}
              disabled={policyLoading}
              className="text-xs text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"
            >
              <svg className={`w-3 h-3 ${policyLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reload Policy
            </button>
          </div>

          {/* Thresholds Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-neutral-700">
                  <th className="py-2 pr-4 text-neutral-400 font-medium">Confidence Level</th>
                  <th className="py-2 px-4 text-green-400 font-medium text-center">RISK_ON</th>
                  <th className="py-2 px-4 text-yellow-400 font-medium text-center">NEUTRAL</th>
                  <th className="py-2 px-4 text-red-400 font-medium text-center">RISK_OFF</th>
                  <th className="py-2 pl-4 text-neutral-400 font-medium text-center">Position %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {[
                  { level: 'EXTREME', key: 'extreme' },
                  { level: 'HIGH', key: 'high' },
                  { level: 'MED_HIGH', key: 'med_high' },
                  { level: 'MED', key: 'med' },
                  { level: 'LOW', key: 'low' },
                  { level: 'MARGINAL', key: 'buy_min' },
                ].map(({ level, key }) => {
                  const positionPct = policyConfidenceLevels?.[level]?.position_pct ?? '-';
                  const dominantRegime = getDominantRegime();
                  const _currentThreshold = policyThresholds[dominantRegime]?.[key as keyof typeof policyThresholds.neutral];
                  const _maxScore = data?.distribution.max ?? 0;
                  void _currentThreshold; // Reserved for future use
                  void _maxScore; // Reserved for future use
                  
                  return (
                    <tr key={level} className="hover:bg-neutral-800/50">
                      <td className="py-2 pr-4 font-medium text-white">{level}</td>
                      <td className={`py-2 px-4 text-center font-mono ${dominantRegime === 'risk_on' ? 'bg-green-900/20' : ''}`}>
                        {policyThresholds.risk_on?.[key as keyof typeof policyThresholds.risk_on]?.toFixed(2) ?? '-'}
                      </td>
                      <td className={`py-2 px-4 text-center font-mono ${dominantRegime === 'neutral' ? 'bg-yellow-900/20' : ''}`}>
                        {policyThresholds.neutral?.[key as keyof typeof policyThresholds.neutral]?.toFixed(2) ?? '-'}
                      </td>
                      <td className={`py-2 px-4 text-center font-mono ${dominantRegime === 'risk_off' ? 'bg-red-900/20' : ''}`}>
                        {policyThresholds.risk_off?.[key as keyof typeof policyThresholds.risk_off]?.toFixed(2) ?? '-'}
                      </td>
                      <td className="py-2 pl-4 text-center text-neutral-300">{positionPct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Comparison with current data */}
          {data && data.distribution.total > 0 && (
            <div className="mt-4 p-4 bg-neutral-800/50 rounded-lg border border-neutral-700">
              <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Your Data vs Policy ({getDominantRegime().toUpperCase().replace('_', ' ')} regime)
              </h4>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {(() => {
                  const dominantRegime = getDominantRegime();
                  const thresholds = policyThresholds[dominantRegime];
                  const maxScore = data.distribution.max;
                  
                  return [
                    { level: 'MARGINAL', key: 'buy_min', color: 'gray' },
                    { level: 'LOW', key: 'low', color: 'blue' },
                    { level: 'MED', key: 'med', color: 'cyan' },
                    { level: 'MED_HIGH', key: 'med_high', color: 'purple' },
                    { level: 'HIGH', key: 'high', color: 'orange' },
                    { level: 'EXTREME', key: 'extreme', color: 'red' },
                  ].map(({ level, key, color: _color }) => {
                    void _color; // Reserved for future styling
                    const threshold = thresholds?.[key as keyof typeof thresholds] ?? 0;
                    const passes = maxScore >= threshold;
                    const margin = maxScore - threshold;
                    
                    return (
                      <div 
                        key={level}
                        className={`p-3 rounded-lg border ${
                          passes 
                            ? 'bg-green-900/20 border-green-500/50' 
                            : 'bg-red-900/20 border-red-500/50'
                        }`}
                      >
                        <p className="text-xs text-neutral-400 mb-1">{level}</p>
                        <p className="text-sm font-mono text-white">{threshold.toFixed(2)}</p>
                        <p className={`text-xs mt-1 font-semibold ${passes ? 'text-green-400' : 'text-red-400'}`}>
                          {passes ? 'PASS' : 'FAIL'} 
                          <span className="font-normal text-neutral-500 ml-1">
                            ({margin >= 0 ? '+' : ''}{margin.toFixed(3)})
                          </span>
                        </p>
                      </div>
                    );
                  });
                })()}
              </div>
              
              <div className="mt-3 flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-neutral-400">Your Max Score:</span>
                  <span className="font-mono font-semibold text-white">{data.distribution.max.toFixed(3)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-neutral-400">Regime:</span>
                  <span className="font-semibold text-yellow-400">{getDominantRegime().toUpperCase().replace('_', ' ')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sector Distribution Table */}
      <div className="bg-neutral-900 rounded-lg p-6 border border-purple-500/30">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
              Sector Distribution
            </h3>
            <p className="text-xs text-neutral-400 mt-1">
              Stocks in current Trading Universe grouped by sector
              {totalStocksInUniverse > 0 && (
                <span className="ml-2 text-purple-400 font-semibold">
                  ({totalStocksInUniverse} total stocks)
                </span>
              )}
            </p>
          </div>
          <button
            onClick={fetchSectorDistribution}
            disabled={sectorLoading}
            className="text-xs text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1"
          >
            <svg className={`w-3 h-3 ${sectorLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {sectorLoading && (
          <div className="flex items-center gap-2 text-neutral-400 py-4">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading sector distribution...
          </div>
        )}

        {!sectorLoading && sectorError && (
          <div className="p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg">
            <p className="text-amber-400 text-sm">{sectorError}</p>
            <p className="text-xs text-neutral-500 mt-1">
              Sector data requires an active stock universe. This may be empty outside market hours.
            </p>
          </div>
        )}

        {!sectorLoading && !sectorError && sectorDistribution.length === 0 && (
          <div className="p-4 bg-neutral-800/50 rounded-lg text-center">
            <p className="text-neutral-400 text-sm">No stocks in universe</p>
            <p className="text-xs text-neutral-500 mt-1">
              The stock universe may be empty outside market hours or if APIs are unavailable.
            </p>
          </div>
        )}

        {!sectorLoading && !sectorError && sectorDistribution.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-neutral-700">
                  <th className="py-2 pr-4 text-neutral-400 font-medium">Sector</th>
                  <th className="py-2 px-4 text-neutral-400 font-medium text-center">Stocks</th>
                  <th className="py-2 px-4 text-neutral-400 font-medium text-center">% of Universe</th>
                  <th className="py-2 px-4 text-neutral-400 font-medium text-center">Avg Score</th>
                  <th className="py-2 pl-4 text-neutral-400 font-medium">Distribution</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {sectorDistribution.map((sector, idx) => {
                  const barColor = idx === 0 ? 'bg-purple-500' : 
                                   idx === 1 ? 'bg-blue-500' : 
                                   idx === 2 ? 'bg-cyan-500' : 
                                   idx === 3 ? 'bg-green-500' : 
                                   idx === 4 ? 'bg-yellow-500' : 
                                   'bg-neutral-500';
                  
                  return (
                    <tr key={sector.sector} className="hover:bg-neutral-800/50">
                      <td className="py-2 pr-4 font-medium text-white">{sector.sector}</td>
                      <td className="py-2 px-4 text-center font-mono text-purple-400 font-semibold">
                        {sector.count}
                      </td>
                      <td className="py-2 px-4 text-center font-mono text-neutral-300">
                        {sector.percentage.toFixed(1)}%
                      </td>
                      <td className="py-2 px-4 text-center font-mono text-cyan-400">
                        {sector.avgScore.toFixed(3)}
                      </td>
                      <td className="py-2 pl-4 w-40">
                        <div className="bg-neutral-700 rounded h-4 overflow-hidden">
                          <div 
                            className={`${barColor} h-full transition-all`}
                            style={{ width: `${sector.percentage}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-neutral-600 bg-neutral-800/30">
                  <td className="py-2 pr-4 font-semibold text-white">Total</td>
                  <td className="py-2 px-4 text-center font-mono text-purple-400 font-bold">
                    {totalStocksInUniverse}
                  </td>
                  <td className="py-2 px-4 text-center font-mono text-neutral-300">100%</td>
                  <td className="py-2 px-4 text-center font-mono text-cyan-400">
                    {sectorDistribution.length > 0 
                      ? (sectorDistribution.reduce((sum, s) => sum + s.avgScore * s.count, 0) / totalStocksInUniverse).toFixed(3)
                      : '-'}
                  </td>
                  <td className="py-2 pl-4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Policy Loading/Error States */}
      {policyLoading && (
        <div className="bg-neutral-900 rounded-lg p-4 border border-neutral-700">
          <div className="flex items-center gap-2 text-neutral-400">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading policy thresholds...
          </div>
        </div>
      )}

      {!policyLoading && policyError && (
        <div className="bg-amber-900/20 border border-amber-500/50 rounded-lg p-4">
          <p className="text-amber-400 text-sm">Failed to load policy thresholds: {policyError}</p>
          <button onClick={fetchPolicy} className="mt-2 text-xs text-amber-300 hover:text-amber-200">
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-neutral-900 rounded-lg p-8">
          <div className="flex flex-col items-center justify-center">
            <svg className="animate-spin h-8 w-8 text-blue-500 mb-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-neutral-400">Loading entry score distribution...</p>
            <p className="text-xs text-neutral-500 mt-1">
              Fetching {daysInput} days of data (limit: {limitInput})
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-6">
          <h3 className="text-red-400 font-semibold mb-2">Error Loading Entry Scores</h3>
          <p className="text-red-300">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && (!data || data.distribution.total === 0) && (
        <div className="bg-neutral-900 rounded-lg p-8">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-neutral-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="text-lg font-semibold text-white mb-2">No Entry Scores Found</h3>
            <p className="text-neutral-400 mb-4">
              No entry scores were recorded in the last {appliedDays} days.
            </p>
            <div className="text-sm text-neutral-500 space-y-1">
              <p>Entry scores are logged when the automated trading system evaluates stocks.</p>
              <p>Try increasing the lookback period or check if automated trading is running.</p>
            </div>
          </div>
        </div>
      )}

      {/* Data Display */}
      {!loading && !error && data && data.distribution.total > 0 && (
        <>
          {/* Statistics */}
          <div className="bg-neutral-900 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Distribution Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">Total Signals</p>
                <p className="text-2xl font-bold text-cyan-400 mt-1">{data.distribution.total}</p>
                <p className="text-xs text-neutral-500 mt-1">{data.metadata.uniqueSignals} unique</p>
              </div>
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">Minimum</p>
                <p className="text-2xl font-bold text-red-400 mt-1">{data.distribution.min.toFixed(3)}</p>
              </div>
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">Average</p>
                <p className="text-2xl font-bold text-yellow-400 mt-1">{data.distribution.avg.toFixed(3)}</p>
              </div>
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">Maximum</p>
                <p className="text-2xl font-bold text-green-400 mt-1">{data.distribution.max.toFixed(3)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">P50 (Median)</p>
                <p className="text-xl font-bold text-white mt-1">{data.distribution.p50.toFixed(3)}</p>
              </div>
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">P70</p>
                <p className="text-xl font-bold text-blue-400 mt-1">{data.distribution.p70.toFixed(3)}</p>
              </div>
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">P85</p>
                <p className="text-xl font-bold text-purple-400 mt-1">{data.distribution.p85.toFixed(3)}</p>
              </div>
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">P90</p>
                <p className="text-xl font-bold text-indigo-400 mt-1">{data.distribution.p90.toFixed(3)}</p>
              </div>
            </div>
          </div>

          {/* Histogram */}
          {data.histogram && data.histogram.length > 0 && (
            <div className="bg-neutral-900 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Score Distribution Histogram</h3>
              <div className="space-y-2">
                {data.histogram.map((bin, idx) => {
                  const maxCount = Math.max(...data.histogram.map(b => b.count));
                  const barWidth = maxCount > 0 ? (bin.count / maxCount) * 100 : 0;
                  
                  return (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="w-32 text-sm text-neutral-300 font-mono">{bin.range}</div>
                      <div className="flex-1 bg-neutral-800 rounded h-6 relative overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-blue-600 to-cyan-400 h-full rounded transition-all"
                          style={{ width: `${barWidth}%` }}
                        />
                        <span className="absolute inset-0 flex items-center justify-center text-xs text-white font-semibold">
                          {bin.count}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Confidence Breakdown */}
          {data.confidenceBreakdown && Object.keys(data.confidenceBreakdown).length > 0 && (
            <div className="bg-neutral-900 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Confidence Breakdown</h3>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(data.confidenceBreakdown).map(([confidence, count]) => (
                  <div key={confidence} className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wide text-neutral-400">{confidence}</p>
                    <p className="text-2xl font-bold mt-1 text-white">{count}</p>
                    <p className="text-xs text-neutral-500 mt-1">
                      {((count / data.distribution.total) * 100).toFixed(1)}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Regime Breakdown */}
          {data.regimeBreakdown && Object.keys(data.regimeBreakdown).length > 0 && (
            <div className="bg-neutral-900 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Regime Breakdown</h3>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(data.regimeBreakdown).map(([regime, count]) => (
                  <div key={regime} className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wide text-neutral-400">{regime.replace('_', ' ')}</p>
                    <p className="text-2xl font-bold mt-1 text-white">{count}</p>
                    <p className="text-xs text-neutral-500 mt-1">
                      {((count / data.distribution.total) * 100).toFixed(1)}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-neutral-900 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Analysis Metadata</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">Date Range</p>
                <p className="text-sm text-white mt-1">
                  {new Date(data.metadata.dateRange.from).toLocaleDateString()} - {new Date(data.metadata.dateRange.to).toLocaleDateString()}
                </p>
              </div>
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">Total Records Found</p>
                <p className="text-2xl font-bold text-cyan-400 mt-1">{data.metadata.totalRecordsFound}</p>
              </div>
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-neutral-400">Analyzed Signals</p>
                <p className="text-2xl font-bold text-green-400 mt-1">{data.metadata.analyzedSignals}</p>
              </div>
            </div>
          </div>

          {/* Suggested Thresholds */}
          <div className="bg-neutral-900 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Suggested Thresholds</h3>
            <p className="text-sm text-neutral-400 mb-4">
              Based on the score distribution, here are recommended entry_model threshold values for your policy:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-sm text-neutral-400 mb-2">Conservative (P85)</p>
                <p className="text-2xl font-bold text-purple-400">{data.suggestedThresholds.conservative.toFixed(3)}</p>
                <p className="text-xs text-neutral-500 mt-1">Fewer trades, higher quality signals</p>
              </div>
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-sm text-neutral-400 mb-2">Balanced (P70)</p>
                <p className="text-2xl font-bold text-blue-400">{data.suggestedThresholds.balanced.toFixed(3)}</p>
                <p className="text-xs text-neutral-500 mt-1">More trades, balanced quality</p>
              </div>
              <div className="bg-neutral-800/60 border border-neutral-800 rounded-lg p-4">
                <p className="text-sm text-neutral-400 mb-2">Aggressive (P50)</p>
                <p className="text-2xl font-bold text-yellow-400">{data.suggestedThresholds.aggressive.toFixed(3)}</p>
                <p className="text-xs text-neutral-500 mt-1">Most trades, lower quality threshold</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-300">
                <strong>How to use:</strong> Update <code className="bg-neutral-800 px-2 py-0.5 rounded text-xs">policy.json</code> entry_model thresholds 
                to one of these values based on your risk preference. Higher thresholds = fewer but higher-quality trades.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EntryScoreDistribution;
